import React from 'react';
import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';
import FocusTrap from '../containers/FocusTrap';
import DragContainer from '../containers/DragContainer.js';
import DragHandle from '../containers/DragHandle.js';
import ButtonGroup from '../buttons/ButtonGroup';
import Transition from '../containers/Transition';
import InteractiveElement from '../containers/InteractiveElement';
import Icon from '@cvent/nucleus-icon';
import { isTouchEnabled } from '../utils/isTouchEnabled';
import { tapOrClick } from '../touchEventHandlers';
import { centerElement } from '../utils/centerElement';
import { resolve, select } from '@cvent/nucleus-dynamic-css';
import { resolveTestId } from '@cvent/nucleus-test-automation';
import Logger from '@cvent/nucleus-logging';
const LOG = new Logger('Dialog');

const flexInlineStyle = {
  display: 'flex',
  flexDirection: 'column'
};

export function requiredWhenOpen(props, propName, componentName) {
  if (props.isOpen && !props[propName]) {
    return new Error(propName + ' in ' + componentName + ' is required when Dialog is Open');
  }
  return null;
}
/**
  The Dialog Component uses the isOpen property to display the child content, if animatiable it displays inside of the
  CSS Transition Component. requestClose is a required function that tells the parent application the Dialogs intent to
  close and should be managed inside parent application.
**/
export default class Dialog extends React.Component {
  static displayName = 'Dialog';
  static propTypes = {
    /** The child content for the dialog. */
    children: requiredWhenOpen,
    /** Dialog ID used to activate close functions, matches state in app. */
    dialogId: PropTypes.string,
    /** Either text string title for default header or custom HTML to render. */
    header: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.node
    ]),
    /** Array of buttons or items displayed at the bottom of the Dialog content. */
    actions: PropTypes.array,
    /** An inital left value for the container. If defined, Dialog will not center automatically. */
    initialLeft: PropTypes.number,
    /** An inital top value for the container. If defined, Dialog will not center automatically. */
    initialTop: PropTypes.number,
    /** Float position of Action buttons. */
    actionAlignment: PropTypes.oneOf(['left', 'right', 'center']),
    /** Display the background overlay when Dialog is visible. */
    isModal: PropTypes.bool,
    /** If this is a full screen Modal **/
    isFullScreen: PropTypes.bool,
    /** Use TranstionGroup based animation for display. */
    isAnimatable: PropTypes.bool,
    /** Abillity to drag the Dialog on the viewport. */
    allowDragging: PropTypes.bool,
    /** Prop used to drive display of Dialog window's visibility. */
    isOpen: PropTypes.bool.isRequired,
    /** Adds an inline style to dragContainer to fix scrolling in mobile view. */
    isFixedHeader: PropTypes.bool,
    /** Callback function to request closing the dialog. */
    requestClose: PropTypes.func,
    /** Flag to indicate whether to close dialog when clicking on overlay. */
    closeDialogOnOverlayClick: PropTypes.bool,
    /** Label for Close button - 'close by default'. */
    closeLabel: PropTypes.string,
    /** Label for Close Icon - Accessability fallback 'close by default'. */
    closeFallback: PropTypes.string,
    /** Timeout used for TranstionGroup leave of Dialog. */
    defaultTimeout: PropTypes.number,
    /** Prop used by assistive technologies (mainly screen readers) to read as dialog's name. 'Dialog' by default. */
    ariaLabel: PropTypes.string,
    /** Available Style Keys. */
    style: PropTypes.shape({
      /** Style applied to the wrapper around the child content. */
      content: PropTypes.object,
      /** Top level style applied to the container if overlay is on. */
      placeholder: PropTypes.object,
      /** Top level style applied to the container if overlay is off. */
      empty: PropTypes.object,
      /** Style applied to the child content supplied to the Transition component. */
      transitionWrapper: PropTypes.object,
      /** Style applied to the header if header prop is passed as a string. */
      header: PropTypes.object,
      /** Style applied to the close button in the header if header prop is passed as a string. */
      exit: PropTypes.object,
      /** Style applied to the overlay if overlay is on. */
      overlay: PropTypes.object,
      /** Style applied to the ButtonGroup component if actions prop is populated. */
      actions: PropTypes.object
    })
  };
  static defaultProps = {
    isModal: true,
    isFullScreen: false,
    isAnimatable: true,
    isFixedHeader: false,
    allowDragging: false,
    defaultTimeout: 500,
    actionAlignment: 'center',
    ariaLabel: 'Dialog',
    closeFallback: 'close'
  };

  constructor(props) {
    super(props);
    this.state = {
      overlay: props.isOpen
    };
    this.centerDialog = this.centerDialog.bind(this);
    this.requestClose = this.requestClose.bind(this);
    this.preventBodyScroll = this.preventBodyScroll.bind(this);
    this.restoreBodyScroll = this.restoreBodyScroll.bind(this);
    this.onKeyUp = this.onKeyUp.bind(this);
    this.onOverlayClick = this.onOverlayClick.bind(this);
    this.cancelClick = this.cancelClick.bind(this);
    this.scrollTop = 0;
    this.width = 0;
    this._isMounted = false;
    this._closeOverlayTimeout = null;
  }

  componentWillMount() {
    if (this.props.isOpen) {
      this.preventBodyScroll();
      this.setState({ overlay: true });
    }
  }
  componentDidMount() {
    // Center dialog window based on browser size and add listender for resizing of browser.
    this._isMounted = true;
    this.centerDialog();
  }
  componentWillReceiveProps(nextProps) {
    // Check to see if new Props were passed in, and change state.overlay accoridingly.
    if (!this.props.isOpen && nextProps.isOpen) {
      this.preventBodyScroll();
      clearTimeout(this._closeOverlayTimeout);
      this.setState({ overlay: true });
    } else if (this.props.isOpen && !nextProps.isOpen) {
      if (window.removeEventListener) {
        window.removeEventListener('resize', this.centerDialog);
      }
      // if it's not animated then just change state for now
      if (!nextProps.isAnimatable) {
        this.restoreBodyScroll();
        this.setState({ overlay: false });
      } else {
        // Allow time for transition to perform leave, then change state
        this._closeOverlayTimeout = setTimeout(() => {
          this.restoreBodyScroll();
          if (this._isMounted) {
            this.setState({ overlay: false });
          }
        }, nextProps.defaultTimeout);
      }
    }
  }
  // Center dialog window based on browser size and add listener for resizing of browser.
  // setTimeout is added to let the content inside the dialog finish rendering so dialog could adjust its height,
  // when max-height, not height, is applied to the content of the dialog.
  componentDidUpdate() {
    setTimeout(() => {
      this.centerDialog();
    }, 0);
  }

  componentWillUnmount() {
    if (window.removeEventListener) {
      window.removeEventListener('resize', this.centerDialog);
    }
    if (this.props.isModal) {
      if (document) {
        document.documentElement.style.overflow = 'visible';
        if (isTouchEnabled()) {
          document.documentElement.style.position = 'static';
        }
      }
    }
    this._isMounted = false;
  }

  onKeyUp(event) {
    // Inform the parent of close request on escape key press.
    if (event.keyCode === 27) {
      // If this component is responding to the escape key press, no parent component should also respond to it.
      event.preventDefault();
      event.stopPropagation();
      this.requestClose(event);
    }
  }

  centerDialog() {
    if (this.props.initialLeft || this.props.initialTop) {
      return;
    }
    if (this.props.isOpen && ReactDOM.findDOMNode(this.refs.dialog)) {
      centerElement(ReactDOM.findDOMNode(this.refs.dialog));
    }
    if (!this.props.allowDragging) {
      if (window && window.addEventListener) {
        window.addEventListener('resize', this.centerDialog);
      }
    }
  }

  preventBodyScroll() {
    const topLevelElement = document.documentElement || document.body.parentNode || document.body;
    this.scrollTop = topLevelElement.scrollTop;
    this.width = topLevelElement.style.width;
    if (this.props.isModal) {
      topLevelElement.style.overflow = 'hidden';
      if (document && isTouchEnabled()) {
        topLevelElement.style.position = 'fixed';
      }
    }
  }

  restoreBodyScroll() {
    const topLevelElement = document.documentElement || document.body.parentNode || document.body;
    topLevelElement.scrollTop = this.scrollTop;
    topLevelElement.style.width = this.width;
    if (this.props.isModal) {
      topLevelElement.style.overflow = 'visible';
      if (document && isTouchEnabled()) {
        topLevelElement.style.position = 'static';
      }
    }
  }
  requestClose(event) {
    event.preventDefault();
    if (this.props.requestClose) {
      this.props.requestClose(this.props.dialogId);
    }
  }

  onOverlayClick() {
    if (this.props.requestClose && this.props.closeDialogOnOverlayClick) {
      this.props.requestClose(this.props.dialogId);
    }
  }

  cancelClick(e) {
    // stop click event inside the dialog from bubbling up and closing the dialog
    if (this.props.closeDialogOnOverlayClick) {
      e.stopPropagation();
    }
  }

  render() {
    const { classes = {}, style = {}, isModal, isAnimatable, isFullScreen,
      isOpen, closeLabel, closeFallback, ariaLabel, isFixedHeader } = this.props;

    let header;
    const fixedHeaderInlineStyle = isFixedHeader ? flexInlineStyle : null;
    if (typeof this.props.header === 'string') {
      header = (
        <DragHandle classes={classes} style={style}>
          <h2 {...resolve(this.props, 'header')} {...resolveTestId(this.props, '-header')}>
            {this.props.header}
            {this.props.requestClose &&
            <InteractiveElement
              {...resolve(this.props, 'exit')}
              onClick={this.requestClose}
              {...tapOrClick(this.selectItem)}
              {...resolveTestId(this.props, '-close-button')}>
              {closeLabel || null}
              <Icon icon={'closeDeleteFilled'} fallbackText={closeFallback} />
            </InteractiveElement>}
          </h2>
        </DragHandle>
      );
    } else {
      header = (
        <DragHandle classes={classes} style={style}>
          {this.props.header}
        </DragHandle>
      );
    }

    let actions;
    if (this.props.actions) {
      actions = (
        <ButtonGroup {...select(this.props, 'actions')} alignment={this.props.actionAlignment}>
          {this.props.actions}
        </ButtonGroup>
      );
    }

    const content = (
      <div
        {...resolveTestId(this.props, '-content')}
        {...resolve(this.props, 'content')}>{this.props.children || <div/>}</div>
    );

    const screenSizeStyles = resolve(this.props, 'dragContainer', isFullScreen ? 'fullScreen' : null);
    const dialogClasses = {
      ...classes,
      dragContainer: screenSizeStyles.className
    };
    const dialogInline = {
      ...style,
      dragContainer: {
        ...screenSizeStyles.style,
        ...fixedHeaderInlineStyle
      }
    };

    const dialogContent = (
      <FocusTrap
        onKeyUp={this.onKeyUp}
        focusOnMount={!isAnimatable}
        role="dialog"
        aria-label={ariaLabel}
        onClick={this.cancelClick}
      >
        <DragContainer ref="dialog"
          allowDragging={this.props.allowDragging}
          initialLeft={this.props.initialLeft}
          initialTop={this.props.initialTop}
          classes={dialogClasses}
          style={dialogInline}>
          {header}
          {content}
          {actions}
        </DragContainer>
      </FocusTrap>
    );

    let transitionStyles = select(this.props, 'transition');
    if (isAnimatable && !Object.keys(transitionStyles.classes).length && !Object.keys(transitionStyles.style).length) {
      LOG.warn('This feature has been deprecated...');
      LOG.warn('Css transitions are being moved out of the core LESS file.');
      LOG.warn('Please see Nucleus Tesbed Dialogs Demo for updated implementation.');
      transitionStyles = {
        classes: this.props.classes,
        style: this.props.style
      };
    }
    const dialogOverlay = (<div {...resolve(this.props, 'overlay')} />);
    return (
      // eslint-disable-next-line
      <div {...resolveTestId(this.props)} {...resolve(this.props, this.state.overlay ? 'placeholder' : 'empty')}
        onClick={this.onOverlayClick}
      >
        {isModal && this.state.overlay ? dialogOverlay : null}
        {isAnimatable ?
          <Transition defaultName={this.constructor.displayName} {...transitionStyles}>
            {isOpen ? [
              <div key="dialog" {...resolve(transitionStyles, 'transitionWrapper')}>
                {dialogContent}
              </div>] : []}
          </Transition>
          : null
        }
        {isOpen && !isAnimatable ? // wrapper to be removed
          <div {...resolve(this.props, 'wrapper', 'dialogContainer')}>
            {dialogContent}
          </div> : null}
      </div>
    );
  }
}
